'use client';

import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import {
  DEFAULT_ALTITUDE_PROFILE,
  ceilingAuthorityLabel,
  clampAltitudeMeters,
  resolveAltitudeProfile,
  type AltitudeProfile,
} from '@/lib/altitude';
import { readOrgSettings, type OrgSettingsResponse } from '@/lib/org-settings';

/**
 * `error` matters as much as `ready`: on failure the values below are platform
 * defaults, NOT this organization's configuration, and callers must not present
 * them as authoritative or write them back.
 */
export type AltitudeProfileStatus = 'loading' | 'ready' | 'error';

export interface AltitudeProfileState {
  profile: AltitudeProfile;
  /** Label for whoever owns the ceiling, e.g. "FAA Part 107". Null if unknown. */
  authority: string | null;
  status: AltitudeProfileStatus;
  /**
   * Persist a new preselected altitude for the whole org. Null unless the
   * profile actually loaded — writing a fallback profile back would replace the
   * org's real ceiling, presets and waiver with platform defaults.
   * Throws on failure; the caller owns the error message.
   */
  saveDefault: ((meters: number) => Promise<void>) | null;
}

/**
 * The org's takeoff-altitude profile, for the dock launch flow.
 *
 * Failure is non-blocking on purpose: if `/api/org` is slow or down, launching
 * still works on the default profile rather than stalling behind a config
 * fetch. An operator with a target on the map should never be gated on
 * settings — but they should be told the config isn't theirs.
 */
export function useAltitudeProfile(): AltitudeProfileState {
  const [state, setState] = useState<Omit<AltitudeProfileState, 'saveDefault'>>({
    profile: DEFAULT_ALTITUDE_PROFILE,
    authority: ceilingAuthorityLabel(undefined),
    status: 'loading',
  });

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        // Shared: one in-flight `/api/org` for every caller that asks while it
        // is open, so two consumers cannot describe different configurations.
        const settings = await readOrgSettings();
        if (!active) return;
        setState({
          profile: resolveAltitudeProfile(settings),
          authority: ceilingAuthorityLabel(settings),
          status: 'ready',
        });
      } catch (err) {
        if (!active) return;
        console.warn('[altitude] falling back to default profile:', err);
        setState((prev) => ({ ...prev, status: 'error' }));
      }
    };

    void load();
    return () => { active = false; };
  }, []);

  const ready = state.status === 'ready';

  const saveDefault = useCallback(async (meters: number) => {
    // Re-read before writing. PUT /api/org replaces settings.altitude wholesale,
    // so writing a profile this session merely held in memory could clobber a
    // change an admin made elsewhere in the meantime.
    const res = await api.get<OrgSettingsResponse>('/api/org');
    const current = resolveAltitudeProfile(res.data?.settings);
    const next: AltitudeProfile = {
      ...current,
      default_m: clampAltitudeMeters(meters, current),
    };
    await api.put('/api/org', { altitude: next });
    setState((prev) => ({
      ...prev,
      profile: next,
      authority: ceilingAuthorityLabel(res.data?.settings),
    }));
  }, []);

  return { ...state, saveDefault: ready ? saveDefault : null };
}
